import React, { createContext, useContext, useEffect, useState } from 'react'

const ThemeContext = createContext()

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme deve ser usado dentro de um ThemeProvider')
  }
  return context
}

const defaultCustomColors = {
  // Cores principais
  primary: '#3b82f6',
  secondary: '#6b7280',
  accent: '#f3f4f6',
  
  // Fundos
  background: '#ffffff',
  card: '#ffffff',
  sidebar: '#f9fafb',
  header: '#ffffff',
  
  // Textos
  foreground: '#1f2937',
  muted: '#6b7280',
  textOnPrimary: '#ffffff',
  textOnSecondary: '#ffffff',
  
  // Bordas e divisores
  border: '#e5e7eb',
  borderHover: '#d1d5db',
  borderFocus: '#3b82f6',
  divider: '#e5e7eb',
  
  // Estados interativos
  hover: '#f3f4f6',
  active: '#e5e7eb',
  focus: '#dbeafe',
  disabled: '#f3f4f6',
  
  // Status e alertas
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#3b82f6',
  
  // Elementos específicos
  input: '#ffffff',
  inputBorder: '#d1d5db',
  button: '#3b82f6',
  buttonText: '#ffffff',
  link: '#3b82f6',
  linkHover: '#2563eb'
}

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme')
    if (savedTheme) {
      return savedTheme
    }
    
    // Verificar preferência do sistema
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'auto'
    }
    
    return 'light'
  })

  const [customColors, setCustomColors] = useState(() => {
    const savedColors = localStorage.getItem('customColors')
    return savedColors ? JSON.parse(savedColors) : defaultCustomColors
  })

  useEffect(() => {
    const root = window.document.documentElement
    
    // Remover classes de tema anteriores
    root.classList.remove('dark', 'custom')
    
    if (theme === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      const updateTheme = (e) => {
        root.classList.toggle('dark', e.matches)
      }
      
      mediaQuery.addEventListener('change', updateTheme)
      root.classList.toggle('dark', mediaQuery.matches)
      
      return () => mediaQuery.removeEventListener('change', updateTheme)
    } else if (theme === 'custom') {
      root.classList.add('custom')
      // Aplicar cores customizadas
      Object.entries(customColors).forEach(([key, value]) => {
        const hsl = hexToHsl(value)
        root.style.setProperty(`--${key}`, hsl)
      })
    } else {
      root.classList.toggle('dark', theme === 'dark')
      // Remover variáveis customizadas
      Object.keys(customColors).forEach(key => {
        root.style.removeProperty(`--${key}`)
      })
    }
    
    localStorage.setItem('theme', theme)
  }, [theme, customColors])

  // Função para converter hex para hsl
  const hexToHsl = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255
    const g = parseInt(hex.slice(3, 5), 16) / 255
    const b = parseInt(hex.slice(5, 7), 16) / 255

    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    let h, s, l = (max + min) / 2

    if (max === min) {
      h = s = 0
    } else {
      const d = max - min
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break
        case g: h = (b - r) / d + 2; break
        case b: h = (r - g) / d + 4; break
      }
      h /= 6
    }

    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
  }

  const toggleTheme = () => {
    setTheme(prev => {
      if (prev === 'light') return 'dark'
      if (prev === 'dark') return 'auto'
      if (prev === 'auto') return 'custom'
      return 'light'
    })
  }

  const updateCustomColor = (colorKey, colorValue) => {
    const newColors = { ...customColors, [colorKey]: colorValue }
    setCustomColors(newColors)
    localStorage.setItem('customColors', JSON.stringify(newColors))
  }

  const resetCustomColors = () => {
    setCustomColors(defaultCustomColors)
    localStorage.setItem('customColors', JSON.stringify(defaultCustomColors))
  }

  const value = {
    theme,
    setTheme,
    toggleTheme,
    customColors,
    updateCustomColor,
    resetCustomColors,
    isDark: theme === 'dark' || (theme === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches),
    isCustom: theme === 'custom'
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}
